import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { fetchComponentsFromAPI } from "../../api/componentsApi";
import {
  FollowUpRule,
  FormAvailability,
  FormEditOption,
  FormConfig,
  createFormConfig,
  deleteFormConfig,
  fetchFormConfig,
  fetchFormConfigs,
  fetchFormConfigsWithMeta,
  fetchFormTargetCollections,
  availabilityToConfigPatch,
  editOptionToConfigPatch,
  formConfigToAvailability,
  formConfigToEditOption,
  updateFormConfig
} from "../../api/formConfigApi";
import {
  PublicFormDraftLog,
  PublicFormSubmissionLog,
  deletePublicFormDraftAdmin,
  deletePublicFormDraftsAdmin,
  fetchPublicFormDraftsAdmin,
  fetchPublicFormSubmissionsAdmin
} from "../../api/publicFormsApi";
import { aclAPI, ACLPermission, bulkPermissionResponseKey, FormConfigAction } from "../../api/aclApi";
import { useAuth } from "../../auth/AuthContext";
import {
  EmailTemplateMeta,
  EmailTemplate,
  EmailTemplateCreate,
  fetchEmailTemplates,
  fetchEmailTemplate,
  createEmailTemplate,
  updateEmailTemplate
} from "../../api/emailTemplatesApi";
import SortableFields from "../SortableFields";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, ClipboardList, Download, Mail, MoreHorizontal, Pencil, Trash2, Upload } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import RichTextEdit from "../../editors/RichTextEdit";

type ComponentMeta = {
  uid: string;
  name: string;
  slug: string;
  fields: any[];
  organization?: string;
};

const buildFieldConfig = (field: any, order: number) => ({
  name: field.name,
  label: field.label || field.name,
  description: field.description || "",
  required: !!field.required,
  hidden: false,
  order
});

const buildDefaultFields = (component: ComponentMeta) => {
  const fields = Array.isArray(component.fields) ? component.fields : [];
  return fields.map((field: any, index: number) =>
    buildFieldConfig(field, typeof field.order === "number" ? field.order : index)
  );
};

const USER_SUBMISSION_HEADER_FORM_TITLE = "formTitle";
const USER_SUBMISSION_HEADER_SUBMISSION_DATE = "submissionDate";

const resolveUserSubmissionShowFormTitle = (config: Pick<FormConfig, "userSubmissionShowFormTitle">) =>
  config.userSubmissionShowFormTitle !== false;

const resolveUserSubmissionShowDate = (config: Pick<FormConfig, "userSubmissionShowDate">) =>
  config.userSubmissionShowDate !== false;

const resolveUserSubmissionHeader = (config: Pick<FormConfig, "userSubmissionHeader">) =>
  typeof config.userSubmissionHeader === "string" && config.userSubmissionHeader.trim() !== ""
    ? config.userSubmissionHeader.trim()
    : USER_SUBMISSION_HEADER_FORM_TITLE;

const countUserSubmissionVisible = (config: FormConfig): number => {
  let count = 0;
  if (resolveUserSubmissionShowFormTitle(config)) count += 1;
  if (resolveUserSubmissionShowDate(config)) count += 1;
  count += config.userSubmissionDisplayFields?.length ?? 0;
  return count;
};

const isUserSubmissionHeaderVisible = (config: FormConfig): boolean => {
  const header = resolveUserSubmissionHeader(config);
  if (header === USER_SUBMISSION_HEADER_FORM_TITLE) {
    return resolveUserSubmissionShowFormTitle(config);
  }
  if (header === USER_SUBMISSION_HEADER_SUBMISSION_DATE) {
    return resolveUserSubmissionShowDate(config);
  }
  return (config.userSubmissionDisplayFields ?? []).includes(header);
};

const permissionHasAction = (permission: ACLPermission, action: string): boolean => {
  if (permission.action === action) return true;
  if (Array.isArray((permission as any).actions)) {
    return (permission as any).actions.includes(action);
  }
  return false;
};

const formatDate = (value: any) => {
  if (!value) {
    return "—";
  }
  if (value instanceof Date) {
    return value.toLocaleString();
  }
  if (typeof value === "object") {
    if (typeof value.toDate === "function") {
      return value.toDate().toLocaleString();
    }
    if (typeof value.seconds === "number") {
      return new Date(value.seconds * 1000).toLocaleString();
    }
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString();
};

const htmlToPlainPreview = (html: string, maxLen = 120): string => {
  const text = (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return "No description";
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
};

const PublicFormsManager = () => {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.admin || user?.info?.admin);
  const [components, setComponents] = useState<ComponentMeta[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [formConfigs, setFormConfigs] = useState<FormConfig[]>([]);
  const [fields, setFields] = useState<FormConfig["fields"]>([]);
  const [selectedField, setSelectedField] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [isCreatingNewForm, setIsCreatingNewForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [aclPermissions, setAclPermissions] = useState<ACLPermission[]>([]);
  const [drafts, setDrafts] = useState<PublicFormDraftLog[]>([]);
  const [submissions, setSubmissions] = useState<PublicFormSubmissionLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [logsFormConfig, setLogsFormConfig] = useState<FormConfig | null>(null);
  const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);
  const [deletingAllDrafts, setDeletingAllDrafts] = useState(false);
  const [formMenuOpenId, setFormMenuOpenId] = useState<string | null>(null);
  const [showImportJsonDialog, setShowImportJsonDialog] = useState(false);
  const [importJsonFields, setImportJsonFields] = useState<FormConfig["fields"]>([]);
  const [importJsonTitle, setImportJsonTitle] = useState("");
  const [importJsonComponentSlug, setImportJsonComponentSlug] = useState("");
  const [importJsonError, setImportJsonError] = useState<string | null>(null);
  const [importJsonMeta, setImportJsonMeta] = useState<Partial<FormConfig> | null>(null);
  const [editableFieldsScope, setEditableFieldsScope] = useState<"all" | "selected">("all");
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateMeta[]>([]);
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);
  const [templateEditorId, setTemplateEditorId] = useState<string | null>(null);
  const [templateEditorContent, setTemplateEditorContent] = useState<EmailTemplate | null>(null);
  const [templateEditorSaving, setTemplateEditorSaving] = useState(false);
  const [templateEditorError, setTemplateEditorError] = useState<string | null>(null);
  const [formActionFlags, setFormActionFlags] = useState<Record<string, Partial<Record<FormConfigAction, boolean>>>>({});
  const [createFormByCollection, setCreateFormByCollection] = useState<Record<string, boolean>>({});
  const [templateEditorCreating, setTemplateEditorCreating] = useState(false);
  const [templateEditorNewDraft, setTemplateEditorNewDraft] = useState<EmailTemplateCreate | null>(null);
  const jsonFileInputRef = useRef<HTMLInputElement>(null);
  const descriptionEditorRef = useRef<HTMLDivElement>(null);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [descriptionEditing, setDescriptionEditing] = useState(false);

  const startDescriptionEdit = useCallback(() => {
    setDescriptionOpen(true);
    setDescriptionEditing(true);
  }, []);

  useEffect(() => {
    if (!descriptionEditing) return;
    const id = window.requestAnimationFrame(() => {
      const editor = descriptionEditorRef.current?.querySelector(".ql-editor") as HTMLElement | null;
      editor?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [descriptionEditing]);

  const selectedComponent = useMemo(
    () => components.find((component) => component.slug === selectedSlug) || null,
    [components, selectedSlug]
  );

  const componentNameBySlug = useMemo(() => {
    return new Map(components.map((component) => [component.slug, component.name]));
  }, [components]);

  const componentIdBySlug = useMemo(() => {
    return new Map(components.map((component) => [component.slug, component.uid]));
  }, [components]);

  const availableFields = useMemo(() => {
    if (!selectedComponent) {
      return [];
    }
    const existingNames = new Set(
      (fields || []).map((field) => field?.name).filter(Boolean)
    );
    return (selectedComponent.fields || []).filter(
      (field: any) => field?.name && !existingNames.has(field.name)
    );
  }, [selectedComponent, fields]);

  // Check which relation fields are missing ACL permissions
  const relationFieldsWithoutAcl = useMemo(() => {
    if (!selectedComponent || !aclPermissions.length) {
      return new Map<string, { label: string; targetComponent: string; reason: string }>();
    }

    const missingAcl = new Map<string, { label: string; targetComponent: string; reason: string }>();
    const componentFields = selectedComponent.fields || [];

    // Build a map of field names to their labels that are visible in the form (not hidden)
    const visibleFieldsMap = new Map(
      fields
        .filter((f) => !f.hidden)
        .map((f) => [f.name, f.label || f.name])
    );

    for (const field of componentFields) {
      if (field.type !== 'relation' && field.type !== 'multiplerelation') {
        continue;
      }

      // Only check fields that are visible in the form config
      if (!visibleFieldsMap.has(field.name)) {
        continue;
      }

      // The related collection slug is stored in field.value or field.table
      const relatedSlug = typeof field.value === 'string' ? field.value : field.table;
      if (!relatedSlug) {
        continue;
      }

      const relatedUid = componentIdBySlug.get(relatedSlug);
      if (!relatedUid) {
        continue;
      }

      const hasPublicPrincipal = (perm: ACLPermission) =>
        perm.whoType === 'ROLE' && String(perm.who).toLowerCase().trim() === 'public';
      const permissionTargetsCollection = (perm: ACLPermission, collectionId: string) => {
        const what = perm.what;
        if (what === '*') return true;
        if (typeof what === 'string') return what === collectionId;
        if (Array.isArray(what)) return what.includes(collectionId);
        return false;
      };
      const hasCollectionPublish = aclPermissions.some((perm) =>
        hasPublicPrincipal(perm)
        && permissionHasAction(perm, 'public_publish_collection')
        && permissionTargetsCollection(perm, relatedUid)
      );
      const hasFieldExpose = aclPermissions.some((perm) =>
        hasPublicPrincipal(perm)
        && permissionHasAction(perm, 'public_expose_fields')
        && permissionTargetsCollection(perm, relatedUid)
      );
      const hasPublicForm = aclPermissions.some((perm) =>
        hasPublicPrincipal(perm)
        && permissionHasAction(perm, 'public_form')
        && permissionTargetsCollection(perm, relatedUid)
      );

      const targetComponent = componentNameBySlug.get(relatedSlug) || relatedSlug;
      const label = visibleFieldsMap.get(field.name) || field.label || field.name;

      if (!hasCollectionPublish && !hasFieldExpose && !hasPublicForm) {
        missingAcl.set(field.name, {
          label,
          targetComponent,
          reason: "Related collection is not exposed for public forms (missing public_form, or public_publish_collection / public_expose_fields)."
        });
      }
    }

    return missingAcl;
  }, [selectedComponent, aclPermissions, componentNameBySlug, componentIdBySlug, fields]);

  // Build warning messages for SortableFields
  const fieldWarnings = useMemo(() => {
    const warnings = new Map<string, string>();
    relationFieldsWithoutAcl.forEach(({ targetComponent, reason }, fieldName) => {
      warnings.set(
        fieldName,
        `Missing ACL: ${reason} This field may not appear in the public form for most users. Related collection: "${targetComponent}".`
      );
    });
    return warnings;
  }, [relationFieldsWithoutAcl]);

  // Get warning for currently selected field
  const selectedFieldWarning = useMemo(() => {
    if (!selectedField) {
      return null;
    }
    return fieldWarnings.get(selectedField.name) || null;
  }, [selectedField, fieldWarnings]);

  const userSubmissionCollectionFields = useMemo(() => {
    if (!selectedComponent) {
      return [];
    }
    return (selectedComponent.fields || []).filter(
      (field: any) => field?.name && field.type !== "instruction"
    );
  }, [selectedComponent]);

  const buildDefaultConfig = useCallback(
    (component: ComponentMeta): FormConfig => ({
      componentUid: component.uid,
      componentSlug: component.slug,
      componentOrganization:
        typeof component.organization === "string" && component.organization.trim() !== ""
          ? component.organization.trim()
          : undefined,
      title: `${component.name} form`,
      description: "",
      successMessage: "",
      closedHeader: "",
      closedMessage: "",
      status: "draft",
      requireLogin: false,
      submissionPolicy: "open",
      singleSubmissionPerEmail: false,
      userSubmissionShowFormTitle: true,
      userSubmissionShowDate: true,
      userSubmissionDisplayFields: [],
      userSubmissionHeader: USER_SUBMISSION_HEADER_FORM_TITLE,
      fields: buildDefaultFields(component)
    }),
    []
  );

  const loadComponents = useCallback(async () => {
    const data = isAdmin
      ? await fetchComponentsFromAPI()
      : await fetchFormTargetCollections();
    setComponents(data as ComponentMeta[]);
    if (data.length > 0 && !selectedSlug) {
      setSelectedSlug(data[0].slug);
    }
  }, [selectedSlug, isAdmin]);

  const loadAclPermissions = useCallback(async () => {
    try {
      const { data: permissions } = await aclAPI.getAllPermissions();
      setAclPermissions(permissions);
    } catch (err: any) {
      console.error("Failed to load ACL permissions:", err);
      // Don't show error to user, just log it - missing ACL data just means we can't show warnings
    }
  }, []);

  const loadFormConfigsList = useCallback(async () => {
    setIsLoadingList(true);
    setError(null);
    try {
      const { data, meta } = await fetchFormConfigsWithMeta();
      setFormConfigs(data);
      if (!isAdmin && Array.isArray(meta?.createFormCollectionUids)) {
        setCreateFormByCollection((prev) => {
          const next = { ...prev };
          for (const collectionUid of meta.createFormCollectionUids || []) {
            if (collectionUid) {
              next[collectionUid] = true;
            }
          }
          return next;
        });
      }
    } catch (err: any) {
      setError(err.message || "Failed to load form configs");
    } finally {
      setIsLoadingList(false);
    }
  }, [isAdmin]);

  const refreshFormPermissions = useCallback(async (configs: FormConfig[], componentList: ComponentMeta[]) => {
    if (isAdmin) {
      const allActions: Partial<Record<FormConfigAction, boolean>> = {
        create_form: true,
        read_form: true,
        update_form: true,
        delete_form: true
      };
      const flags: Record<string, Partial<Record<FormConfigAction, boolean>>> = {};
      for (const form of configs) {
        if (form.uid) {
          flags[form.uid] = { ...allActions };
        }
      }
      const createFlags: Record<string, boolean> = {};
      for (const component of componentList) {
        createFlags[component.uid] = true;
      }
      setFormActionFlags(flags);
      setCreateFormByCollection(createFlags);
      return;
    }

    const requests: Parameters<typeof aclAPI.checkBulkPermissions>[0] = [];
    for (const form of configs) {
      if (!form.uid) {
        continue;
      }
      for (const action of ["read_form", "update_form", "delete_form"] as const) {
        requests.push({
          action,
          targetType: "form",
          targetId: form.uid,
          collectionUid: form.componentUid
        });
      }
    }
    for (const component of componentList) {
      requests.push({
        action: "create_form",
        targetType: "form",
        targetId: component.uid,
        collectionUid: component.uid
      });
    }

    if (requests.length === 0) {
      setFormActionFlags({});
      setCreateFormByCollection({});
      return;
    }

    try {
      const results = await aclAPI.checkBulkPermissions(requests);
      const flags: Record<string, Partial<Record<FormConfigAction, boolean>>> = {};
      for (const form of configs) {
        if (!form.uid) {
          continue;
        }
        flags[form.uid] = {
          read_form: results[bulkPermissionResponseKey({
            action: "read_form",
            targetType: "form",
            targetId: form.uid,
            collectionUid: form.componentUid
          })] === true,
          update_form: results[bulkPermissionResponseKey({
            action: "update_form",
            targetType: "form",
            targetId: form.uid,
            collectionUid: form.componentUid
          })] === true,
          delete_form: results[bulkPermissionResponseKey({
            action: "delete_form",
            targetType: "form",
            targetId: form.uid,
            collectionUid: form.componentUid
          })] === true
        };
      }
      const createFlags: Record<string, boolean> = {};
      for (const component of componentList) {
        createFlags[component.uid] = results[bulkPermissionResponseKey({
          action: "create_form",
          targetType: "form",
          targetId: component.uid,
          collectionUid: component.uid
        })] === true;
      }
      setFormActionFlags(flags);
      setCreateFormByCollection(createFlags);
    } catch (err) {
      console.error("Failed to load form ACL flags:", err);
      setFormActionFlags({});
      setCreateFormByCollection({});
    }
  }, [isAdmin]);

  const canCreateFormForCollection = useCallback((collectionUid?: string) => {
    if (isAdmin) {
      return true;
    }
    if (!collectionUid) {
      return false;
    }
    return createFormByCollection[collectionUid] === true;
  }, [isAdmin, createFormByCollection]);

  const canViewForm = useCallback((form?: FormConfig | null) => {
    if (isAdmin) {
      return Boolean(form?.uid) || isAdmin;
    }
    if (!form?.uid) {
      return false;
    }
    const flags = formActionFlags[form.uid];
    return flags?.read_form === true || flags?.update_form === true || flags?.delete_form === true;
  }, [isAdmin, formActionFlags]);

  const canReadForm = canViewForm;

  const canUpdateForm = useCallback((form?: FormConfig | null) => {
    if (isAdmin) {
      return true;
    }
    if (!form?.uid) {
      return false;
    }
    return formActionFlags[form.uid]?.update_form === true;
  }, [isAdmin, formActionFlags]);

  const canDeleteForm = useCallback((form: FormConfig) => {
    if (isAdmin) {
      return true;
    }
    if (!form.uid) {
      return false;
    }
    return formActionFlags[form.uid]?.delete_form === true;
  }, [isAdmin, formActionFlags]);

  const canSaveCurrentForm = useMemo(() => {
    if (!formConfig) {
      return false;
    }
    if (formConfig.uid) {
      return canUpdateForm(formConfig);
    }
    const collectionUid = selectedComponent?.uid ?? formConfig.componentUid;
    return canCreateFormForCollection(collectionUid);
  }, [formConfig, selectedComponent, canUpdateForm, canCreateFormForCollection]);

  const editorReadOnly = useMemo(() => {
    if (isAdmin) {
      return false;
    }
    if (formConfig?.uid) {
      return !canUpdateForm(formConfig);
    }
    return !canCreateFormForCollection(selectedComponent?.uid ?? formConfig?.componentUid);
  }, [isAdmin, formConfig, selectedComponent, canUpdateForm, canCreateFormForCollection]);

  const canCreateAnyForm = useMemo(() => {
    if (isAdmin) {
      return true;
    }
    return Object.values(createFormByCollection).some(Boolean);
  }, [isAdmin, createFormByCollection]);

  const loadFormConfigForSelectedForm = useCallback(async () => {
    if (!selectedFormId) {
      return;
    }
    setError(null);
    setIsCreatingNewForm(false);
    try {
      const config = await fetchFormConfig(selectedFormId);
      setFormConfig({ ...config, requireLogin: false });
      setFields(config.fields || []);
    } catch (err: any) {
      setError(err.message || "Failed to load form config");
    }
  }, [selectedFormId]);

  const loadFormConfigForSlug = useCallback(async () => {
    if (selectedFormId) {
      return;
    }
    if (!selectedSlug) {
      return;
    }
    setError(null);

    if (isCreatingNewForm && selectedComponent) {
      const defaultConfig = buildDefaultConfig(selectedComponent);
      setFormConfig(defaultConfig);
      setFields(defaultConfig.fields);
      return;
    }

    const configs = await fetchFormConfigs(selectedSlug);
    if (configs.length > 0) {
      const config = configs[0];
      setFormConfig({ ...config, requireLogin: false });
      setFields(config.fields || []);
    } else if (selectedComponent) {
      const defaultConfig = buildDefaultConfig(selectedComponent);
      setFormConfig(defaultConfig);
      setFields(defaultConfig.fields);
    }
  }, [selectedSlug, selectedComponent, selectedFormId, buildDefaultConfig, isCreatingNewForm]);

  const loadFormLogs = useCallback(async (publicToken?: string) => {
    if (!publicToken) {
      setDrafts([]);
      setSubmissions([]);
      return;
    }
    setLogsLoading(true);
    setLogsError(null);
    try {
      const [draftItems, submissionItems] = await Promise.all([
        fetchPublicFormDraftsAdmin(publicToken),
        fetchPublicFormSubmissionsAdmin(publicToken)
      ]);
      setDrafts(draftItems);
      setSubmissions(submissionItems);
    } catch (err: any) {
      setLogsError(err.message || "Failed to load logs");
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadComponents().catch((err) => setError(err.message || "Failed to load components"));
  }, [loadComponents]);

  useEffect(() => {
    loadAclPermissions();
  }, [loadAclPermissions]);

  useEffect(() => {
    if (showEditor) {
      loadAclPermissions();
    }
  }, [showEditor, loadAclPermissions]);

  const loadEmailTemplatesList = useCallback(async () => {
    try {
      const list = await fetchEmailTemplates();
      setEmailTemplates(list);
    } catch {
      setEmailTemplates([]);
    }
  }, []);

  useEffect(() => {
    if (showEditor) {
      loadEmailTemplatesList();
    }
  }, [showEditor, loadEmailTemplatesList]);

  useEffect(() => {
    if (showTemplatesDialog && emailTemplates.length === 0) {
      loadEmailTemplatesList();
    }
  }, [showTemplatesDialog, emailTemplates.length, loadEmailTemplatesList]);

  useEffect(() => {
    if (!showLogsDialog) {
      return;
    }
    loadFormLogs(logsFormConfig?.publicToken);
  }, [showLogsDialog, logsFormConfig?.publicToken, loadFormLogs]);

  useEffect(() => {
    loadFormConfigsList().catch((err) => setError(err.message || "Failed to load form configs"));
  }, [loadFormConfigsList]);

  useEffect(() => {
    refreshFormPermissions(formConfigs, components).catch(() => { });
  }, [formConfigs, components, refreshFormPermissions]);

  useEffect(() => {
    if (!selectedFormId) {
      return;
    }
    loadFormConfigForSelectedForm().catch((err) => setError(err.message || "Failed to load form config"));
  }, [selectedFormId, loadFormConfigForSelectedForm]);

  useEffect(() => {
    const names = formConfig?.editableFieldsOnUpdate;
    setEditableFieldsScope(
      Array.isArray(names) && names.length > 0 ? "selected" : "all"
    );
  }, [formConfig?.uid]);

  useEffect(() => {
    if (selectedFormId) {
      return;
    }
    loadFormConfigForSlug().catch((err) => setError(err.message || "Failed to load form config"));
  }, [selectedFormId, loadFormConfigForSlug]);

  const handleFieldChange = (field: any) => {
    setSelectedField(field);
  };

  const updateSelectedField = (updates: Partial<any>) => {
    if (!selectedField) {
      return;
    }
    const updated = { ...selectedField, ...updates };
    setSelectedField(updated);
    setFields((prev) =>
      prev.map((field) => (field.name === selectedField.name ? updated : field))
    );
  };

  const handleSortChange = (sorted: any[]) => {
    setFields(sorted);
  };

  const handleFollowUpRuleChange = (index: number, updates: Partial<FollowUpRule>) => {
    setFormConfig((prev) => {
      if (!prev) {
        return prev;
      }
      const currentRules = Array.isArray(prev.followUpRules) ? [...prev.followUpRules] : [];
      const existing = currentRules[index] || {
        triggerField: "",
        triggerValue: "",
        followUpFormUid: "",
        completionField: "",
        completionValue: "",
        delivery: { email: true, inApp: true }
      };
      currentRules[index] = { ...existing, ...updates };
      return { ...prev, followUpRules: currentRules };
    });
  };

  const handleAddFollowUpRule = () => {
    setFormConfig((prev) => {
      if (!prev) {
        return prev;
      }
      const currentRules = Array.isArray(prev.followUpRules) ? [...prev.followUpRules] : [];
      currentRules.push({
        triggerField: "",
        triggerValue: "",
        followUpFormUid: "",
        completionField: "",
        completionValue: "",
        delivery: { email: true, inApp: true }
      });
      return { ...prev, followUpRules: currentRules };
    });
  };

  const handleRemoveFollowUpRule = (index: number) => {
    setFormConfig((prev) => {
      if (!prev) {
        return prev;
      }
      const currentRules = Array.isArray(prev.followUpRules) ? [...prev.followUpRules] : [];
      currentRules.splice(index, 1);
      return { ...prev, followUpRules: currentRules };
    });
  };

  const handleAddField = (componentField: any) => {
    if (!componentField?.name) {
      return;
    }
    setFields((prev) => {
      if (prev.some((field) => field.name === componentField.name)) {
        return prev;
      }
      const maxOrder = prev.reduce((max, field) => {
        if (typeof field.order === "number") {
          return Math.max(max, field.order);
        }
        return max;
      }, -1);
      const nextOrder = maxOrder + 1;
      const newField = buildFieldConfig(componentField, nextOrder);
      setSelectedField(newField);
      return [...prev, newField];
    });
  };

  const handleAddAllFields = () => {
    if (!availableFields.length) {
      return;
    }
    const existingNames = new Set((fields || []).map((f) => f?.name).filter(Boolean));
    const toAdd = availableFields.filter((f: any) => f?.name && !existingNames.has(f.name));
    if (!toAdd.length) {
      return;
    }
    const maxOrder = (fields || []).reduce((max, f) => (typeof f?.order === "number" ? Math.max(max, f.order) : max), -1);
    let nextOrder = maxOrder + 1;
    const newFields = toAdd.map((componentField: any) => buildFieldConfig(componentField, nextOrder++));
    const nextFields = [...(fields || []), ...newFields].map((f, i) => (f ? { ...f, order: i } : f)).filter(Boolean);
    setFields(nextFields);
    setFormConfig((prev) => (prev ? { ...prev, fields: nextFields } : null));
  };

  const handleRemoveField = (field: any) => {
    if (!field?.name) {
      return;
    }
    const nextFields = (fields || [])
      .filter((f) => f.name !== field.name)
      .map((f, i) => (f ? { ...f, order: i } : f))
      .filter(Boolean);
    setFields(nextFields);
    setFormConfig((prev) => (prev ? { ...prev, fields: nextFields } : null));
    if (selectedField?.name === field.name) {
      setSelectedField(null);
    }
  };

  const handleNewForm = () => {
    if (!selectedComponent) {
      setError("Select a component before creating a form.");
      return;
    }
    if (!canCreateFormForCollection(selectedComponent.uid)) {
      setError("You do not have permission to create form configurations for this collection.");
      return;
    }
    setSelectedFormId(null);
    setSelectedField(null);
    setIsCreatingNewForm(true);
    const defaultConfig = buildDefaultConfig(selectedComponent);
    setFormConfig(defaultConfig);
    setFields(defaultConfig.fields);
    setDescriptionOpen(false);
    setDescriptionEditing(false);
    setShowEditor(true);
  };

  const handleEditForm = (config: FormConfig) => {
    if (!config.uid) {
      setError("This form config cannot be edited.");
      return;
    }
    if (!canViewForm(config) && !canUpdateForm(config)) {
      setError("You do not have permission to view this form configuration.");
      return;
    }
    setIsCreatingNewForm(false);
    setSelectedFormId(config.uid);
    if (config.componentSlug && config.componentSlug !== selectedSlug) {
      setSelectedSlug(config.componentSlug);
    }
    setDescriptionOpen(false);
    setDescriptionEditing(false);
    setShowEditor(true);
  };

  const handleDeleteForm = async (config: FormConfig) => {
    if (!config.uid) {
      setError("This form config cannot be deleted.");
      return;
    }
    if (!canDeleteForm(config)) {
      setError("You do not have permission to delete this form configuration.");
      return;
    }
    const confirmed = window.confirm(`Delete "${config.title}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await deleteFormConfig(config.uid);
      if (selectedFormId === config.uid) {
        setSelectedFormId(null);
        if (selectedComponent) {
          const defaultConfig = buildDefaultConfig(selectedComponent);
          setFormConfig(defaultConfig);
          setFields(defaultConfig.fields);
        } else {
          setFormConfig(null);
          setFields([]);
        }
      }
      await loadFormConfigsList();
    } catch (err: any) {
      setError(err.message || "Failed to delete form config");
    } finally {
      setIsSaving(false);
    }
  };

  const exportFormAsJson = async (config: FormConfig) => {
    if (!config.uid) {
      setError("This form cannot be exported.");
      return;
    }
    setFormMenuOpenId(null);
    try {
      const full = await fetchFormConfig(config.uid);
      const payload = {
        ...full,
        exportedAt: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const name = (full.title || "form").replace(/[^a-z0-9_-]/gi, "_");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}-public-form.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || "Failed to export form");
    }
  };

  const parseJsonToImport = (data: unknown): { fields: FormConfig["fields"]; meta: Partial<FormConfig> } | null => {
    if (!data || typeof data !== "object") return null;
    const obj = data as Record<string, unknown>;
    const rawFields = obj.fields;
    if (!Array.isArray(rawFields) || rawFields.length === 0) return null;
    const fields: FormConfig["fields"] = rawFields.map((f: any, i: number) => ({
      name: typeof f?.name === "string" ? f.name.trim() : "",
      label: typeof f?.label === "string" ? f.label : (typeof f?.name === "string" ? f.name : ""),
      description: typeof f?.description === "string" ? f.description : "",
      required: !!f?.required,
      hidden: !!f?.hidden,
      order: i
    })).filter((f) => f.name.length > 0);
    if (fields.length === 0) return null;
    const meta: Partial<FormConfig> = {};
    if (typeof obj.title === "string" && obj.title.trim()) meta.title = obj.title.trim();
    if (typeof obj.description === "string") meta.description = obj.description;
    if (typeof obj.successMessage === "string") meta.successMessage = obj.successMessage;
    if (typeof obj.closedHeader === "string") meta.closedHeader = obj.closedHeader;
    if (typeof obj.closedMessage === "string") meta.closedMessage = obj.closedMessage;
    if (obj.status === "draft" || obj.status === "published" || obj.status === "archived") meta.status = obj.status;
    if (typeof obj.requireLogin === "boolean") meta.requireLogin = obj.requireLogin;
    if (typeof obj.singleSubmissionPerEmail === "boolean") meta.singleSubmissionPerEmail = obj.singleSubmissionPerEmail;
    if (obj.submissionPolicy === "open" || obj.submissionPolicy === "overwrite" || obj.submissionPolicy === "duplicate") {
      meta.submissionPolicy = obj.submissionPolicy;
    }
    if (typeof obj.regenerateAutoFieldsOnUpdate === "boolean") {
      meta.regenerateAutoFieldsOnUpdate = obj.regenerateAutoFieldsOnUpdate;
    }
    if (Array.isArray(obj.editableFieldsOnUpdate)) {
      meta.editableFieldsOnUpdate = obj.editableFieldsOnUpdate.filter(
        (name): name is string => typeof name === "string" && name.trim().length > 0
      );
    }
    if (typeof obj.subtitle === "string") meta.subtitle = obj.subtitle;
    if (typeof obj.iconUrl === "string") meta.iconUrl = obj.iconUrl;
    if (typeof obj.firstNameField === "string") meta.firstNameField = obj.firstNameField;
    if (typeof obj.lastNameField === "string") meta.lastNameField = obj.lastNameField;
    if (typeof obj.userSubmissionShowFormTitle === "boolean") {
      meta.userSubmissionShowFormTitle = obj.userSubmissionShowFormTitle;
    }
    if (typeof obj.userSubmissionShowDate === "boolean") {
      meta.userSubmissionShowDate = obj.userSubmissionShowDate;
    }
    if (Array.isArray(obj.userSubmissionDisplayFields)) {
      meta.userSubmissionDisplayFields = obj.userSubmissionDisplayFields.filter(
        (name): name is string => typeof name === "string" && name.trim().length > 0
      );
    }
    if (typeof obj.userSubmissionHeader === "string" && obj.userSubmissionHeader.trim()) {
      meta.userSubmissionHeader = obj.userSubmissionHeader.trim();
    }
    if (typeof obj.componentOrganization === "string" && obj.componentOrganization.trim()) {
      meta.componentOrganization = obj.componentOrganization.trim();
    }
    if (Array.isArray(obj.followUpRules)) meta.followUpRules = obj.followUpRules as FollowUpRule[];
    return { fields, meta };
  };

  const startImportFromJson = () => {
    setImportJsonFields([]);
    setImportJsonTitle("");
    setImportJsonComponentSlug(components[0]?.slug ?? "");
    setImportJsonError(null);
    setImportJsonMeta(null);
    setShowImportJsonDialog(false);
    jsonFileInputRef.current?.click();
  };

  const handleJsonFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      let data: unknown;
      try {
        data = JSON.parse(text);
      } catch {
        setImportJsonError("Invalid JSON. Upload a form config JSON (e.g. exported from Export as JSON).");
        setShowImportJsonDialog(true);
        setImportJsonFields([]);
        return;
      }
      const parsed = parseJsonToImport(data);
      if (!parsed) {
        setImportJsonError("Invalid JSON. Must contain a non-empty \"fields\" array (each field needs \"name\").");
        setShowImportJsonDialog(true);
        setImportJsonFields([]);
        return;
      }
      const componentSlug = typeof (data as Record<string, unknown>).componentSlug === "string"
        ? (data as Record<string, unknown>).componentSlug as string
        : components[0]?.slug ?? "";
      const resolvedSlug = components.some((c) => c.slug === componentSlug) ? componentSlug : (components[0]?.slug ?? "");
      setImportJsonFields(parsed.fields);
      setImportJsonTitle(parsed.meta.title || (components[0] ? `${components[0].name} form` : "Imported form"));
      setImportJsonComponentSlug(resolvedSlug);
      setImportJsonMeta(parsed.meta);
      setImportJsonError(null);
      setShowImportJsonDialog(true);
    };
    reader.readAsText(file);
  };

  const confirmImportFromJson = async () => {
    const component = components.find((c) => c.slug === importJsonComponentSlug);
    if (!component || !importJsonTitle.trim()) {
      setImportJsonError("Select a component and enter a form title.");
      return;
    }
    setIsSaving(true);
    setImportJsonError(null);
    try {
      const payload: FormConfig = {
        componentUid: component.uid,
        componentSlug: component.slug,
        title: importJsonTitle.trim(),
        description: importJsonMeta?.description ?? "",
        successMessage: importJsonMeta?.successMessage ?? "",
        ...(typeof importJsonMeta?.closedHeader === "string"
          ? { closedHeader: importJsonMeta.closedHeader }
          : {}),
        ...(typeof importJsonMeta?.closedMessage === "string"
          ? { closedMessage: importJsonMeta.closedMessage }
          : {}),
        status: importJsonMeta?.status ?? "draft",
        requireLogin: false,
        singleSubmissionPerEmail: importJsonMeta?.singleSubmissionPerEmail ?? false,
        ...(importJsonMeta?.submissionPolicy !== undefined
          ? { submissionPolicy: importJsonMeta.submissionPolicy }
          : {}),
        ...(importJsonMeta?.regenerateAutoFieldsOnUpdate !== undefined
          ? { regenerateAutoFieldsOnUpdate: importJsonMeta.regenerateAutoFieldsOnUpdate }
          : {}),
        fields: importJsonFields.map((f, i) => ({ ...f, order: i })),
        ...(importJsonMeta?.subtitle !== undefined && { subtitle: importJsonMeta.subtitle }),
        ...(importJsonMeta?.iconUrl !== undefined && { iconUrl: importJsonMeta.iconUrl }),
        ...(importJsonMeta?.firstNameField !== undefined && { firstNameField: importJsonMeta.firstNameField }),
        ...(importJsonMeta?.lastNameField !== undefined && { lastNameField: importJsonMeta.lastNameField }),
        ...(importJsonMeta?.userSubmissionShowFormTitle !== undefined && {
          userSubmissionShowFormTitle: importJsonMeta.userSubmissionShowFormTitle
        }),
        ...(importJsonMeta?.userSubmissionShowDate !== undefined && {
          userSubmissionShowDate: importJsonMeta.userSubmissionShowDate
        }),
        ...(importJsonMeta?.userSubmissionDisplayFields !== undefined && {
          userSubmissionDisplayFields: importJsonMeta.userSubmissionDisplayFields
        }),
        ...(importJsonMeta?.userSubmissionHeader !== undefined && {
          userSubmissionHeader: importJsonMeta.userSubmissionHeader
        }),
        ...(importJsonMeta?.followUpRules !== undefined && { followUpRules: importJsonMeta.followUpRules })
      };
      const saved = await createFormConfig(payload);
      setShowImportJsonDialog(false);
      setImportJsonFields([]);
      setImportJsonMeta(null);
      await loadFormConfigsList();
      handleEditForm(saved);
    } catch (err: any) {
      setImportJsonError(err?.message || "Failed to create form from JSON");
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewLogs = (config: FormConfig) => {
    setLogsFormConfig(config);
    setShowLogsDialog(true);
  };

  const handleDeleteDraft = async (draftId: string) => {
    if (!logsFormConfig?.publicToken) {
      setLogsError("Save the form to generate a public token before deleting drafts.");
      return;
    }
    const confirmed = window.confirm("Delete this draft? This cannot be undone.");
    if (!confirmed) {
      return;
    }
    setDeletingDraftId(draftId);
    setLogsError(null);
    try {
      await deletePublicFormDraftAdmin(logsFormConfig.publicToken, draftId);
      setDrafts((prev) => prev.filter((draft) => draft.uid !== draftId));
    } catch (err: any) {
      setLogsError(err.message || "Failed to delete draft");
    } finally {
      setDeletingDraftId(null);
    }
  };

  const handleDeleteAllDrafts = async () => {
    if (!logsFormConfig?.publicToken) {
      setLogsError("Save the form to generate a public token before deleting drafts.");
      return;
    }
    const confirmed = window.confirm("Delete all drafts for this form? This cannot be undone.");
    if (!confirmed) {
      return;
    }
    setDeletingAllDrafts(true);
    setLogsError(null);
    try {
      await deletePublicFormDraftsAdmin(logsFormConfig.publicToken);
      setDrafts([]);
    } catch (err: any) {
      setLogsError(err.message || "Failed to delete drafts");
    } finally {
      setDeletingAllDrafts(false);
    }
  };

  const handleSave = async () => {
    if (!formConfig) {
      setError("Nothing to save.");
      return;
    }
    const componentUid = selectedComponent?.uid ?? formConfig.componentUid;
    const componentSlug = selectedComponent?.slug ?? formConfig.componentSlug;
    const componentOrganization = selectedComponent
      ? typeof selectedComponent.organization === "string" && selectedComponent.organization.trim() !== ""
        ? selectedComponent.organization.trim()
        : undefined
      : formConfig.componentOrganization;
    if (!componentUid || !componentSlug) {
      setError(
        "Cannot save: collection is not loaded. Wait for the list to finish loading or pick a collection again."
      );
      return;
    }
    const editOption = formConfigToEditOption(formConfig);
    if (
      editableFieldsScope === "selected" &&
      (editOption === "overwrite" || editOption === "duplicate") &&
      !(formConfig.editableFieldsOnUpdate?.length ?? 0)
    ) {
      setError("Select at least one field editable on update, or switch to All fields.");
      return;
    }
    if (countUserSubmissionVisible(formConfig) === 0) {
      setError(
        "Select at least one item to show in user submissions (form title, date, or a field)."
      );
      return;
    }
    if (!isUserSubmissionHeaderVisible(formConfig)) {
      setError("The header must be one of the visible user submission items.");
      return;
    }
    setIsSaving(true);
    setError(null);
    const wasUpdate = Boolean(formConfig.uid);
    try {
      const editableOnUpdate =
        editOption === "no_edit"
          ? []
          : editableFieldsScope === "all"
            ? []
            : formConfig.editableFieldsOnUpdate;
      const payload: FormConfig = {
        ...formConfig,
        componentUid,
        componentSlug,
        componentOrganization,
        fields,
        requireLogin: false,
        editableFieldsOnUpdate: editableOnUpdate,
        ...(editOption === "no_edit" ? { regenerateAutoFieldsOnUpdate: false } : {})
      };
      const saved = formConfig.uid
        ? await updateFormConfig(formConfig.uid, payload)
        : await createFormConfig(payload);
      setFormConfig(saved);
      setFields(saved.fields || []);
      setEditableFieldsScope(
        Array.isArray(saved.editableFieldsOnUpdate) && saved.editableFieldsOnUpdate.length > 0
          ? "selected"
          : "all"
      );
      if (saved.uid) {
        setSelectedFormId(saved.uid);
      }
      await loadFormConfigsList();
      await loadFormLogs(saved.publicToken);
      setDescriptionOpen(false);
      setDescriptionEditing(false);
      setShowEditor(false);
      toast.success(wasUpdate ? "Form saved." : "Form created.");
    } catch (err: any) {
      setError(err.message || "Failed to save form config");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyPublicUrl = async () => {
    if (!publicUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 2000);
    } catch {
      setError("Failed to copy public URL");
    }
  };

  const handleShowForm = (config: FormConfig) => {
    if (!config.publicToken) {
      setError("Save the form to generate a public token before opening it.");
      return;
    }
    const targetUrl = `/public/forms/${config.publicToken}`;
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  const publicUrl = formConfig?.publicToken ? `/forms/${formConfig.publicToken}` : "";

  const formEditorContent = (
    <div className="space-y-6 pb-8">
      {error && <p className="text-sm text-red-500">{error}</p>}
      {editorReadOnly && (
        <Alert>
          <AlertDescription>You have read-only access to this form configuration.</AlertDescription>
        </Alert>
      )}
      <Tabs defaultValue="general" className="w-full">
        <div className="sticky top-0 z-20 bg-white pb-3 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200">
          <TabsList className="grid grid-cols-4 w-auto !bg-white rounded-none p-0 h-auto border-0">
            <TabsTrigger value="general" className="bg-gray-100 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-200">General</TabsTrigger>
            <TabsTrigger value="fields" className="bg-gray-100 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-200">Fields</TabsTrigger>
            <TabsTrigger value="email" className="bg-gray-100 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-200">Email templates</TabsTrigger>
            <TabsTrigger value="followup" className="bg-gray-100 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-200">Follow up</TabsTrigger>
          </TabsList>
          {canSaveCurrentForm && (
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="!bg-blue-600 !text-white hover:!bg-blue-700 shrink-0"
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          )}
        </div>
        <TabsContent value="general" className="mt-4">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:items-start">
            <Card className="xl:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Data source</CardTitle>
                <CardDescription>Collection that receives submissions from this form.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 bg-white">
                <Label>Component</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={selectedSlug}
                  onChange={(event) => {
                    const nextSlug = event.target.value;
                    setSelectedSlug(nextSlug);
                    if (isCreatingNewForm) {
                      const nextComponent =
                        components.find((component) => component.slug === nextSlug) || null;
                      if (nextComponent) {
                        const nextConfig = buildDefaultConfig(nextComponent);
                        setFormConfig(nextConfig);
                        setFields(nextConfig.fields);
                      }
                    } else {
                      const nextComponent =
                        components.find((component) => component.slug === nextSlug) || null;
                      if (nextComponent && formConfig) {
                        setFormConfig((prev) => {
                          if (!prev) {
                            return prev;
                          }
                          return {
                            ...prev,
                            componentUid: nextComponent.uid,
                            componentSlug: nextComponent.slug,
                            componentOrganization:
                              typeof nextComponent.organization === "string" &&
                                nextComponent.organization.trim() !== ""
                                ? nextComponent.organization.trim()
                                : undefined
                          };
                        });
                      }
                    }
                  }}
                >
                  {components.map((component) => (
                    <option key={component.uid} value={component.slug}>
                      {component.name}
                    </option>
                  ))}
                </select>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Title & subtitle</CardTitle>
                <CardDescription>Shown in the public form header and above the fields.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 bg-white">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={formConfig?.title || ""}
                      onChange={(event) =>
                        setFormConfig((prev) => (prev ? { ...prev, title: event.target.value } : prev))
                      }
                      placeholder="Shown in top bar"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtitle</Label>
                    <Input
                      value={formConfig?.subtitle || ""}
                      onChange={(event) =>
                        setFormConfig((prev) => (prev ? { ...prev, subtitle: event.target.value } : prev))
                      }
                      placeholder="Shown above the form body"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Availability & editing</CardTitle>
                <CardDescription>Whether the form is live and how returning visitors may update submissions.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 bg-white">
                <div className="space-y-2">
                  <Label>Form availability</Label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={formConfig ? formConfigToAvailability(formConfig) : "draft"}
                    onChange={(event) => {
                      const availability = event.target.value as FormAvailability;
                      setFormConfig((prev) =>
                        prev ? { ...prev, ...availabilityToConfigPatch(availability) } : prev
                      );
                    }}
                  >
                    <option value="draft">Draft — public URL returns 404</option>
                    <option value="closed">Closed — show closed message to visitors</option>
                    <option value="open">Open — show the form fields</option>
                  </select>
                  <p className="text-xs text-gray-500">
                    Draft returns 404 on the public URL. Closed shows your heading and message instead of fields. Open loads the interactive form fields.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Edit options</Label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={formConfig ? formConfigToEditOption(formConfig) : "no_edit"}
                    onChange={(event) => {
                      const edit = event.target.value as FormEditOption;
                      setFormConfig((prev) =>
                        prev ? { ...prev, ...editOptionToConfigPatch(edit) } : prev
                      );
                      if (edit === "no_edit") {
                        setEditableFieldsScope("all");
                      }
                    }}
                  >
                    <option value="no_edit">
                      No edit — only new submissions (repeat visitors add another submission; no overwrite
                      workflow)
                    </option>
                    <option value="overwrite">Edit allowed — updates the current submission</option>
                    <option value="duplicate">Edit allowed — each save creates a new submission</option>
                  </select>
                  <p className="text-xs text-gray-500">
                    Edit modes prefill earlier answers for signed-in respondents and use overwrite or duplicate on the server.
                  </p>
                </div>
                {formConfig &&
                  (formConfigToEditOption(formConfig) === "overwrite" ||
                    formConfigToEditOption(formConfig) === "duplicate") ? (
                  <>
                    <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50/80 px-3 py-2">
                      <Checkbox
                        id="regenerateAutoFieldsOnUpdate"
                        className="mt-0.5"
                        checked={!!formConfig?.regenerateAutoFieldsOnUpdate}
                        onCheckedChange={(checked) =>
                          setFormConfig((prev) =>
                            prev
                              ? { ...prev, regenerateAutoFieldsOnUpdate: checked === true }
                              : prev
                          )
                        }
                      />
                      <label
                        htmlFor="regenerateAutoFieldsOnUpdate"
                        className="text-sm leading-snug cursor-pointer min-w-0"
                      >
                        <span className="font-medium text-gray-900">
                          Regenerate auto fields on resubmit
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          When someone updates via edit mode, replace Code fields (and future server auto
                          fields) with newly generated values instead of keeping the previous ones.
                        </span>
                      </label>
                    </div>
                    <div className="space-y-2">
                      <Label className="pr-2">Fields editable on update</Label>
                      <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
                        <button
                          type="button"
                          className={`rounded px-3 py-1.5 text-sm transition-colors ${editableFieldsScope === "all"
                            ? "bg-white font-medium text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                            }`}
                          onClick={() => {
                            setEditableFieldsScope("all");
                            setFormConfig((prev) =>
                              prev ? { ...prev, editableFieldsOnUpdate: [] } : prev
                            );
                          }}
                        >
                          All fields
                        </button>
                        <button
                          type="button"
                          className={`rounded px-3 py-1.5 text-sm transition-colors ${editableFieldsScope === "selected"
                            ? "bg-white font-medium text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                            }`}
                          onClick={() => {
                            setEditableFieldsScope("selected");
                            setFormConfig((prev) =>
                              prev
                                ? {
                                  ...prev,
                                  editableFieldsOnUpdate: Array.isArray(prev.editableFieldsOnUpdate)
                                    ? prev.editableFieldsOnUpdate
                                    : []
                                }
                                : prev
                            );
                          }}
                        >
                          Selected fields
                        </button>
                      </div>
                      {editableFieldsScope === "selected" ? (
                        <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border border-gray-200 px-3 py-2">
                          {(formConfig.fields || []).map((field) => {
                            const checked = (formConfig.editableFieldsOnUpdate ?? []).includes(field.name);
                            const inputId = `editable-on-update-${field.name}`;
                            return (
                              <div key={field.name} className="flex items-center gap-2">
                                <Checkbox
                                  id={inputId}
                                  checked={checked}
                                  onCheckedChange={(isChecked) =>
                                    setFormConfig((prev) => {
                                      if (!prev) return prev;
                                      const current = prev.editableFieldsOnUpdate ?? [];
                                      const next =
                                        isChecked === true
                                          ? current.includes(field.name)
                                            ? current
                                            : [...current, field.name]
                                          : current.filter((name) => name !== field.name);
                                      return { ...prev, editableFieldsOnUpdate: next };
                                    })
                                  }
                                />
                                <label htmlFor={inputId} className="text-sm cursor-pointer">
                                  {field.label || field.name}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">
                          Respondents can change any field when updating a submission.
                        </p>
                      )}
                    </div>
                  </>
                ) : null}
              </CardContent>
            </Card>

            {formConfig && formConfigToAvailability(formConfig) === "closed" ? (
              <Card className="xl:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Closed state</CardTitle>
                  <CardDescription>Content shown when the form is closed to new submissions.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 bg-white">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
                    <div className="space-y-2">
                      <Label>Heading for visitors</Label>
                      <Input
                        value={formConfig.closedHeader || ""}
                        onChange={(event) =>
                          setFormConfig((prev) =>
                            prev ? { ...prev, closedHeader: event.target.value } : prev
                          )
                        }
                        placeholder="Form closed"
                      />
                      <p className="text-xs text-gray-500">
                        Shown above the message. Leave empty to use the default &quot;Form closed&quot;.
                      </p>
                    </div>
                    <div className="space-y-2 lg:col-span-1">
                      <Label>Message for visitors</Label>
                      <div className="min-h-[100px]">
                        <RichTextEdit
                          name="closedMessage"
                          value={formConfig.closedMessage || ""}
                          onChange={(payload) =>
                            setFormConfig((prev) => (prev ? { ...prev, closedMessage: payload.value } : prev))
                          }
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        If empty, visitors see: &quot;This form is not accepting submissions right now.&quot;
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : null}

            <Card className="xl:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Presentation</CardTitle>
                <CardDescription>Branding and intro copy on the public form page.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 bg-white">
                <div className="space-y-2 max-w-xl">
                  <Label>Icon image URL</Label>
                  <Input
                    value={formConfig?.iconUrl || ""}
                    onChange={(event) =>
                      setFormConfig((prev) => (prev ? { ...prev, iconUrl: event.target.value } : prev))
                    }
                    placeholder="Optional image URL for top bar"
                  />
                </div>
                <div
                  className={`rounded-md border border-gray-200 overflow-hidden ${descriptionEditing ? "" : "cursor-pointer hover:border-gray-300 transition-colors"
                    }`}
                  onMouseDown={(event) => {
                    if (descriptionEditing) return;
                    if ((event.target as HTMLElement).closest("button")) return;
                    event.preventDefault();
                    startDescriptionEdit();
                  }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-gray-50/80 border-b border-gray-200">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900 min-w-0 flex-1">
                      <button
                        type="button"
                        className="shrink-0 rounded p-0.5 hover:bg-gray-200/80"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (descriptionEditing) return;
                          setDescriptionOpen((open) => !open);
                        }}
                        aria-expanded={descriptionOpen}
                        aria-label={descriptionOpen ? "Collapse description" : "Expand description"}
                      >
                        {descriptionOpen ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                      <span className="truncate">Description</span>
                      {!descriptionOpen && (
                        <span className="font-normal text-gray-500 truncate">
                          — {htmlToPlainPreview(formConfig?.description || "")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {descriptionEditing ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation();
                            setDescriptionEditing(false);
                          }}
                        >
                          Done
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-500 hidden sm:inline">Click to edit</span>
                      )}
                    </div>
                  </div>
                  {descriptionOpen && (
                    <div className="p-3">
                      {descriptionEditing ? (
                        <div ref={descriptionEditorRef} className="min-h-[120px]">
                          <RichTextEdit
                            name="description"
                            value={formConfig?.description || ""}
                            onChange={(payload) =>
                              setFormConfig((prev) =>
                                prev ? { ...prev, description: payload.value } : prev
                              )
                            }
                          />
                        </div>
                      ) : (
                        <div
                          className="prose prose-sm max-w-none min-h-[3rem] text-gray-800 rounded border border-gray-100 bg-white px-3 py-2 [&_a]:text-blue-600"
                          dangerouslySetInnerHTML={{
                            __html: (formConfig?.description || "").trim()
                              ? formConfig?.description || ""
                              : "<p class=\"text-gray-500\">No description</p>"
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">After submit</CardTitle>
                <CardDescription>Confirmation copy and submit button label.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 bg-white">
                <div className="space-y-2">
                  <Label>Success message</Label>
                  <div className="min-h-[100px]">
                    <RichTextEdit
                      name="successMessage"
                      value={formConfig?.successMessage || ""}
                      onChange={(payload) =>
                        setFormConfig((prev) => (prev ? { ...prev, successMessage: payload.value } : prev))
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Next button label</Label>
                  <Input
                    placeholder="Next"
                    value={formConfig?.nextButtonLabel ?? ""}
                    onChange={(event) =>
                      setFormConfig((prev) =>
                        prev ? { ...prev, nextButtonLabel: event.target.value || undefined } : prev
                      )
                    }
                  />
                  <p className="text-xs text-gray-500">Label for the form submit button (default: Next)</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Access & registration</CardTitle>
                <CardDescription>Name fields for signup prefill.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 bg-white">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>First name field</Label>
                    <select
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={formConfig?.firstNameField ?? ""}
                      onChange={(e) =>
                        setFormConfig((prev) =>
                          prev ? { ...prev, firstNameField: e.target.value || undefined } : prev
                        )
                      }
                    >
                      <option value="">None</option>
                      {(formConfig?.fields || []).map((f) => (
                        <option key={f.name} value={f.name}>
                          {f.label || f.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500">Prefill first name on registration</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Last name field</Label>
                    <select
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={formConfig?.lastNameField ?? ""}
                      onChange={(e) =>
                        setFormConfig((prev) =>
                          prev ? { ...prev, lastNameField: e.target.value || undefined } : prev
                        )
                      }
                    >
                      <option value="">None</option>
                      {(formConfig?.fields || []).map((f) => (
                        <option key={f.name} value={f.name}>
                          {f.label || f.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500">Prefill last name on registration</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50/50 px-3 py-2.5 opacity-60">
                  <Checkbox
                    id="requireLogin"
                    className="mt-0.5"
                    checked={false}
                    disabled
                    aria-disabled
                  />
                  <label htmlFor="requireLogin" className="text-sm cursor-not-allowed">
                    <span className="font-medium text-gray-700">Require login before submission</span>
                    <span className="block text-xs text-gray-500 mt-0.5 font-normal">
                      Disabled — public forms do not require login.
                    </span>
                  </label>
                </div>
              </CardContent>
            </Card>

            <Card className="xl:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">User submissions</CardTitle>
                <CardDescription>
                  Choose what logged-in users see in Submissions and Recent submissions.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 bg-white">
                {!selectedComponent ? (
                  <p className="text-sm text-gray-500">
                    Select a collection to configure display fields.
                  </p>
                ) : formConfig ? (
                  <>
                    <div className="grid grid-cols-[3rem_3.5rem_1fr] gap-x-2 gap-y-0 text-xs font-medium text-gray-500 px-1">
                      <span>Show</span>
                      <span>Header</span>
                      <span>Label</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                      {(() => {
                        const headerKey = resolveUserSubmissionHeader(formConfig);
                        const visibleCount = countUserSubmissionVisible(formConfig);
                        const showFormTitle = resolveUserSubmissionShowFormTitle(formConfig);
                        const showDate = resolveUserSubmissionShowDate(formConfig);
                        const displayFields = formConfig.userSubmissionDisplayFields ?? [];

                        const setHeader = (key: string) => {
                          setFormConfig((prev) => {
                            if (!prev) return prev;
                            const next: FormConfig = { ...prev, userSubmissionHeader: key };
                            if (key === USER_SUBMISSION_HEADER_FORM_TITLE) {
                              next.userSubmissionShowFormTitle = true;
                            } else if (key === USER_SUBMISSION_HEADER_SUBMISSION_DATE) {
                              next.userSubmissionShowDate = true;
                            } else {
                              const current = prev.userSubmissionDisplayFields ?? [];
                              if (!current.includes(key)) {
                                next.userSubmissionDisplayFields = [...current, key];
                              }
                            }
                            return next;
                          });
                        };

                        const renderRow = (
                          rowKey: string,
                          label: string,
                          showChecked: boolean,
                          onShowChange: (checked: boolean) => void
                        ) => {
                          const isHeader = headerKey === rowKey;
                          const canUncheckShow =
                            showChecked && !isHeader && visibleCount > 1;
                          return (
                            <div
                              key={rowKey}
                              className="grid grid-cols-[3rem_3.5rem_1fr] items-center gap-x-2 border-b border-gray-100 px-3 py-2 last:border-b-0"
                            >
                              <Checkbox
                                checked={showChecked}
                                disabled={showChecked && !canUncheckShow}
                                onCheckedChange={(checked) => {
                                  if (checked === false && (!canUncheckShow || isHeader)) {
                                    return;
                                  }
                                  onShowChange(checked === true);
                                }}
                                aria-label={`Show ${label}`}
                              />
                              <input
                                type="radio"
                                name="userSubmissionHeader"
                                checked={isHeader}
                                onChange={() => setHeader(rowKey)}
                                aria-label={`Use ${label} as header`}
                                className="h-4 w-4"
                              />
                              <span className="text-sm text-gray-900">{label}</span>
                            </div>
                          );
                        };

                        return (
                          <>
                            {renderRow(
                              USER_SUBMISSION_HEADER_FORM_TITLE,
                              `Form title (${formConfig.title?.trim() || "Form title"})`,
                              showFormTitle,
                              (checked) =>
                                setFormConfig((prev) =>
                                  prev
                                    ? { ...prev, userSubmissionShowFormTitle: checked }
                                    : prev
                                )
                            )}
                            {renderRow(
                              USER_SUBMISSION_HEADER_SUBMISSION_DATE,
                              "Submission time/date",
                              showDate,
                              (checked) =>
                                setFormConfig((prev) =>
                                  prev ? { ...prev, userSubmissionShowDate: checked } : prev
                                )
                            )}
                            {userSubmissionCollectionFields.map((field: any) => {
                              const fieldName = String(field.name);
                              const fieldLabel = field.label || fieldName;
                              const showField = displayFields.includes(fieldName);
                              return renderRow(fieldName, fieldLabel, showField, (checked) => {
                                setFormConfig((prev) => {
                                  if (!prev) return prev;
                                  const current = prev.userSubmissionDisplayFields ?? [];
                                  const next = checked
                                    ? current.includes(fieldName)
                                      ? current
                                      : [...current, fieldName]
                                    : current.filter((name) => name !== fieldName);
                                  return { ...prev, userSubmissionDisplayFields: next };
                                });
                              });
                            })}
                          </>
                        );
                      })()}
                    </div>
                  </>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="email" className="mt-4">
          <Card className="max-w-3xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email templates
              </CardTitle>
              <CardDescription>
                Verification and completion emails sent for this form.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 bg-white">
              <p className="text-sm text-gray-600">
                Choose which email templates to use for the verification (magic link) and completion (after submit) emails for this form.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Verification email</Label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={formConfig?.verificationTemplateId ?? ""}
                    onChange={(e) =>
                      setFormConfig((prev) =>
                        prev
                          ? {
                            ...prev,
                            verificationTemplateId: e.target.value || undefined
                          }
                          : prev
                      )
                    }
                  >
                    <option value="">Default (Complete your submission)</option>
                    {emailTemplates
                      .filter((t) => t.type === "verification")
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                          {t.builtIn ? " (built-in)" : ""}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Completion email</Label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={formConfig?.completionTemplateId ?? ""}
                    onChange={(e) =>
                      setFormConfig((prev) =>
                        prev
                          ? {
                            ...prev,
                            completionTemplateId: e.target.value || undefined
                          }
                          : prev
                      )
                    }
                  >
                    <option value="">Default (Submission complete)</option>
                    {emailTemplates
                      .filter((t) => t.type === "completion")
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                          {t.builtIn ? " (built-in)" : ""}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setTemplateEditorId(null);
                  setTemplateEditorContent(null);
                  setTemplateEditorError(null);
                  setShowTemplatesDialog(true);
                }}
              >
                Manage email templates
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="followup" className="mt-4 space-y-4">
          {(formConfig?.followUpRules || []).length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-gray-500 bg-white">
                No follow-up rules configured. Add a rule to trigger another form when a field matches a value.
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 xl:items-start">
            {(formConfig?.followUpRules || []).map((rule, index) => (
              <Card key={`followup-${index}`}>
                <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3 space-y-0">
                  <div className="min-w-0">
                    <CardTitle className="text-base">Rule {index + 1}</CardTitle>
                    <CardDescription className="mt-1">
                      When a field matches, invite another form.
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 shrink-0"
                    onClick={() => handleRemoveFollowUpRule(index)}
                  >
                    Remove
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4 bg-white">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Trigger field</Label>
                      <select
                        className="w-full border rounded px-3 py-2 text-sm"
                        value={rule.triggerField || ""}
                        onChange={(event) =>
                          handleFollowUpRuleChange(index, { triggerField: event.target.value })
                        }
                      >
                        <option value="">Select field</option>
                        {(selectedComponent?.fields || []).map((field: any) => (
                          <option key={field.name} value={field.name}>
                            {field.label || field.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Trigger value (case-sensitive)</Label>
                      <Input
                        value={rule.triggerValue || ""}
                        onChange={(event) =>
                          handleFollowUpRuleChange(index, { triggerValue: event.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Follow-up form</Label>
                      <select
                        className="w-full border rounded px-3 py-2 text-sm"
                        value={rule.followUpFormUid || ""}
                        onChange={(event) =>
                          handleFollowUpRuleChange(index, { followUpFormUid: event.target.value })
                        }
                      >
                        <option value="">Select form</option>
                        {formConfigs
                          .filter((config) => Boolean(config.uid))
                          .filter((config) => !selectedSlug || config.componentSlug === selectedSlug)
                          .map((config) => (
                            <option key={config.uid} value={config.uid}>
                              {config.title}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Completion field</Label>
                      <select
                        className="w-full border rounded px-3 py-2 text-sm"
                        value={rule.completionField || ""}
                        onChange={(event) =>
                          handleFollowUpRuleChange(index, { completionField: event.target.value })
                        }
                      >
                        <option value="">Select field</option>
                        {(selectedComponent?.fields || []).map((field: any) => (
                          <option key={field.name} value={field.name}>
                            {field.label || field.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Completion value</Label>
                      <Input
                        value={rule.completionValue || ""}
                        onChange={(event) =>
                          handleFollowUpRuleChange(index, { completionValue: event.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 pt-1 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`followup-email-${index}`}
                        checked={rule.delivery?.email !== false}
                        onCheckedChange={(checked) =>
                          handleFollowUpRuleChange(index, {
                            delivery: { ...rule.delivery, email: checked === true }
                          })
                        }
                      />
                      <Label htmlFor={`followup-email-${index}`}>Email user</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`followup-inapp-${index}`}
                        checked={rule.delivery?.inApp !== false}
                        onCheckedChange={(checked) =>
                          handleFollowUpRuleChange(index, {
                            delivery: { ...rule.delivery, inApp: checked === true }
                          })
                        }
                      />
                      <Label htmlFor={`followup-inapp-${index}`}>Show in app</Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Button type="button" variant="outline" onClick={handleAddFollowUpRule}>
            Add follow-up rule
          </Button>
        </TabsContent>
        <TabsContent value="fields" className="mt-4 flex flex-col h-[calc(100vh-14rem)] min-h-[400px]">
          <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto space-y-6 flex flex-col pr-1">
              <Card className="bg-white shrink-0">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Form fields</CardTitle>
                  <CardDescription>Drag to reorder; click a field to edit settings.</CardDescription>
                </CardHeader>
                <CardContent>
                  {relationFieldsWithoutAcl.size > 0 && (
                    <Alert variant="destructive" className="mb-4 border-amber-200 bg-amber-50 text-amber-800">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800">
                        <strong>Missing ACL permissions:</strong> The following relation fields will not be visible because related collection visibility is not granted for public payloads:
                        <ul className="mt-2 list-disc list-inside">
                          {Array.from(relationFieldsWithoutAcl.entries()).map(([fieldName, { label, targetComponent }]) => (
                            <li key={fieldName}>
                              <strong>{label}</strong> → {targetComponent}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2 text-xs">
                          In ACL Manager, add WHO Type "ROLE", Identifier "public", with ACTION "public_form" for published forms, or "public_publish_collection" / "public_expose_fields" on the related collection. Generic public feeds still require "public_publish_collection" or field exposure.
                        </p>
                      </AlertDescription>
                    </Alert>
                  )}
                  <SortableFields
                    fields={fields}
                    onFieldChange={handleFieldChange}
                    onSortChange={handleSortChange}
                    fieldWarnings={fieldWarnings}
                    onRemoveField={handleRemoveField}
                  />
                </CardContent>
              </Card>

              <Card key={`available-fields-${(fields || []).map((f) => f.name).join(",")}`} className="shrink-0">
                <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3 space-y-0">
                  <div className="min-w-0">
                    <CardTitle className="text-base">Available fields</CardTitle>
                    <CardDescription>Component fields not yet on this form.</CardDescription>
                  </div>
                  {availableFields.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={handleAddAllFields}
                    >
                      Add all
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {availableFields.length === 0 && (
                    <p className="text-sm text-gray-500">No new fields to add.</p>
                  )}
                  {availableFields.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {availableFields.map((field: any) => (
                        <div
                          key={field.name}
                          className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50/60 px-3 py-2 min-w-0"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {field.name}
                            </p>
                            {field.label && field.label !== field.name && field.label.length < 80 && (
                              <p className="text-xs text-gray-500 truncate">{field.label}</p>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="shrink-0 h-7 px-2 text-xs"
                            onClick={() => handleAddField(field)}
                          >
                            Add
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="lg:w-[320px] shrink-0 self-start">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Field settings</CardTitle>
                <CardDescription>Overrides for the selected field.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {!selectedField && <p className="text-sm text-gray-500">Select a field to edit.</p>}
                {selectedField && (
                  <>
                    {selectedFieldWarning && (
                      <Alert variant="destructive" className="border-amber-200 bg-amber-50 text-amber-800">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-800 text-xs">
                          {selectedFieldWarning}
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-1">
                      <Label>Label</Label>
                      <Input
                        value={selectedField.label || ""}
                        onChange={(event) => updateSelectedField({ label: event.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Description</Label>
                      <textarea
                        className="w-full border rounded px-3 py-2 text-sm"
                        value={selectedField.description || ""}
                        onChange={(event) => updateSelectedField({ description: event.target.value })}
                      />
                    </div>
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={!!selectedField.required}
                        onCheckedChange={(checked) =>
                          updateSelectedField({ required: checked === true })
                        }
                      />
                      <Label>Required</Label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={!!selectedField.hidden}
                        onCheckedChange={(checked) =>
                          updateSelectedField({ hidden: checked === true })
                        }
                      />
                      <Label>Hide field</Label>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <>
      {showEditor ? (
        <div className="flex flex-col mx-4 my-6 md:mx-6 md:my-8 h-[calc(100vh-8rem)] min-h-0">
          <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-4">
            <div className="flex flex-wrap items-center gap-3 min-w-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDescriptionOpen(false);
                  setDescriptionEditing(false);
                  setShowEditor(false);
                }}
                className="gap-2"
                aria-label="Back to list"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to forms
              </Button>
              <h1 className="text-xl font-semibold">{selectedFormId ? "Edit form" : "New form"}</h1>
            </div>
            {publicUrl ? (
              <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                <span className="truncate">
                  <span className="text-gray-500">Public URL:</span> <span className="font-mono">{publicUrl}</span>
                </span>
                <button
                  type="button"
                  onClick={handleCopyPublicUrl}
                  className="inline-flex items-center rounded border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 shrink-0"
                >
                  {isCopied ? "Copied" : "Copy"}
                </button>
              </div>
            ) : (
              <span className="text-sm text-gray-500">Public URL will appear after saving.</span>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            {formEditorContent}
          </div>
        </div>
      ) : (
        <div className="space-y-6 mx-4 my-6 md:mx-6 md:my-8">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>Public Forms</CardTitle>
                  <CardDescription className="mt-1.5">
                    Configure published forms, fields, and email templates.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canCreateAnyForm && (
                    <Button onClick={handleNewForm} className="sm:w-auto bg-blue-600 text-white hover:bg-blue-700">
                      New form
                    </Button>
                  )}
                  {canCreateAnyForm && (
                    <Button onClick={startImportFromJson} variant="outline" className="sm:w-auto" disabled={components.length === 0}>
                      <Upload className="mr-2 h-4 w-4" />
                      Import form from JSON
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 bg-white">
              {error && <p className="text-sm text-red-500">{error}</p>}

              {isLoadingList && (
                <p className="text-sm text-gray-500 py-6 text-center">Loading forms...</p>
              )}
              {!isLoadingList && formConfigs.length === 0 && (
                <p className="text-sm text-gray-500 py-6 text-center rounded-lg border border-dashed border-gray-200 bg-gray-50/50">
                  No forms configured yet. Create one to publish a public link.
                </p>
              )}
              {!isLoadingList && formConfigs.length > 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {formConfigs.map((config) => {
                    const componentName =
                      componentNameBySlug.get(config.componentSlug) || config.componentSlug;
                    const formUrl = config.publicToken ? `/forms/${config.publicToken}` : null;
                    const statusLabel = formConfigToAvailability(config);
                    return (
                      <Card
                        key={config.uid || `${config.componentSlug}-${config.title}`}
                        className="overflow-hidden transition-shadow hover:shadow-md cursor-default"
                        onDoubleClick={() => handleEditForm(config)}
                      >
                        <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2 space-y-0">
                          <div className="min-w-0 flex-1">
                            <CardTitle className="text-base truncate" title={config.title}>
                              {config.title}
                            </CardTitle>
                            <CardDescription className="truncate mt-1" title={componentName}>
                              {componentName}
                            </CardDescription>
                          </div>
                          <DropdownMenu open={formMenuOpenId === (config.uid ?? config.publicToken)} onOpenChange={(open) => setFormMenuOpenId(open ? (config.uid ?? config.publicToken ?? '') : null)}>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0"
                                aria-label="Open form actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem onClick={() => { setFormMenuOpenId(null); handleViewLogs(config); }}>
                                <ClipboardList className="mr-2 h-4 w-4" />
                                Drafts & logs
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setFormMenuOpenId(null); handleShowForm(config); }} disabled={!config.publicToken}>
                                Show form
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setFormMenuOpenId(null); handleEditForm(config); }} disabled={!canViewForm(config) && !canUpdateForm(config)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => exportFormAsJson(config)}>
                                <Download className="mr-2 h-4 w-4" />
                                Export as JSON
                              </DropdownMenuItem>
                              {canDeleteForm(config) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => { setFormMenuOpenId(null); handleDeleteForm(config); }}
                                    disabled={isSaving}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </CardHeader>
                        <CardContent className="space-y-3 pt-0 text-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusLabel === "open"
                                ? "bg-green-50 text-green-800 ring-1 ring-green-200"
                                : statusLabel === "closed"
                                  ? "bg-gray-100 text-gray-600 ring-1 ring-gray-200"
                                  : "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
                                }`}
                            >
                              {statusLabel}
                            </span>
                          </div>
                          {formUrl ? (
                            <a
                              href={formUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block font-mono text-xs text-blue-600 hover:underline break-all"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {formUrl}
                            </a>
                          ) : (
                            <p className="text-xs text-gray-500">Save to generate public URL</p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

            </CardContent>
          </Card>
        </div>
      )}
      <input
        ref={jsonFileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={handleJsonFileChange}
      />
      <Dialog open={showImportJsonDialog} onOpenChange={(open) => {
        if (!open) {
          setImportJsonError(null);
          setImportJsonFields([]);
          setImportJsonMeta(null);
        }
        setShowImportJsonDialog(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import form from JSON</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              JSON must contain a <code className="rounded bg-gray-100 px-1">fields</code> array (each field needs <code className="rounded bg-gray-100 px-1">name</code>; optional: <code className="rounded bg-gray-100 px-1">label</code>, <code className="rounded bg-gray-100 px-1">description</code>, <code className="rounded bg-gray-100 px-1">required</code>, <code className="rounded bg-gray-100 px-1">hidden</code>). You can use a file exported via Export as JSON.
            </p>
            {importJsonFields.length > 0 && (
              <>
                <div className="space-y-2">
                  <Label>Component</Label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={importJsonComponentSlug}
                    onChange={(e) => setImportJsonComponentSlug(e.target.value)}
                  >
                    {components.map((c) => (
                      <option key={c.uid} value={c.slug}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Form title</Label>
                  <Input
                    value={importJsonTitle}
                    onChange={(e) => setImportJsonTitle(e.target.value)}
                    placeholder="Form title"
                  />
                </div>
                <p className="text-xs text-gray-500">{importJsonFields.length} field(s) will be created.</p>
              </>
            )}
            {importJsonError && (
              <Alert variant="destructive" className="py-2">
                <AlertDescription>{importJsonError}</AlertDescription>
              </Alert>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowImportJsonDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmImportFromJson}
              disabled={isSaving || importJsonFields.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSaving ? "Creating…" : "Create form"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showTemplatesDialog}
        onOpenChange={(open) => {
          if (!open) {
            setTemplateEditorId(null);
            setTemplateEditorContent(null);
            setTemplateEditorError(null);
            setTemplateEditorNewDraft(null);
          }
          setShowTemplatesDialog(open);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email templates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {templateEditorNewDraft ? (
              <div className="space-y-4 border rounded p-4 bg-gray-50">
                <p className="text-sm font-medium text-gray-700">New template</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={templateEditorNewDraft.name}
                      onChange={(e) =>
                        setTemplateEditorNewDraft((prev) =>
                          prev ? { ...prev, name: e.target.value } : null
                        )
                      }
                      placeholder="e.g. My verification email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <select
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={templateEditorNewDraft.type}
                      onChange={(e) =>
                        setTemplateEditorNewDraft((prev) =>
                          prev
                            ? { ...prev, type: e.target.value as 'verification' | 'completion' }
                            : null
                        )
                      }
                    >
                      <option value="verification">Verification (magic link)</option>
                      <option value="completion">Completion (after submit)</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    value={templateEditorNewDraft.subject}
                    onChange={(e) =>
                      setTemplateEditorNewDraft((prev) =>
                        prev ? { ...prev, subject: e.target.value } : null
                      )
                    }
                    placeholder="Email subject"
                  />
                </div>
                <div className="space-y-2">
                  <Label>HTML body</Label>
                  <textarea
                    className="w-full border rounded px-3 py-2 text-sm font-mono min-h-[200px]"
                    value={templateEditorNewDraft.html}
                    onChange={(e) =>
                      setTemplateEditorNewDraft((prev) =>
                        prev ? { ...prev, html: e.target.value } : null
                      )
                    }
                    placeholder="HTML content"
                    spellCheck={false}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Plain text body</Label>
                  <textarea
                    className="w-full border rounded px-3 py-2 text-sm font-mono min-h-[120px]"
                    value={templateEditorNewDraft.text}
                    onChange={(e) =>
                      setTemplateEditorNewDraft((prev) =>
                        prev ? { ...prev, text: e.target.value } : null
                      )
                    }
                    placeholder="Plain text fallback"
                    spellCheck={false}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Verification: use {"{{registrationUrl}}"}; completion: use {"{{baseUrl}}"}, {"{{formSummary}}"}.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setTemplateEditorNewDraft(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={templateEditorSaving || !templateEditorNewDraft.name.trim()}
                    onClick={async () => {
                      if (!templateEditorNewDraft || !templateEditorNewDraft.name.trim()) return;
                      setTemplateEditorSaving(true);
                      setTemplateEditorError(null);
                      try {
                        const created = await createEmailTemplate(templateEditorNewDraft);
                        await loadEmailTemplatesList();
                        setTemplateEditorNewDraft(null);
                        setTemplateEditorId(created.id);
                        const t = await fetchEmailTemplate(created.id);
                        setTemplateEditorContent(t);
                        if (formConfig && showEditor) {
                          setEmailTemplates(await fetchEmailTemplates());
                        }
                      } catch (err: unknown) {
                        setTemplateEditorError(err instanceof Error ? err.message : "Failed to create");
                      } finally {
                        setTemplateEditorSaving(false);
                      }
                    }}
                  >
                    {templateEditorSaving ? "Creating…" : "Create template"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <div className="flex-1 space-y-2">
                    <Label>Select template to edit</Label>
                    <select
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={templateEditorId ?? ""}
                      onChange={async (e) => {
                        const id = e.target.value || null;
                        setTemplateEditorId(id);
                        setTemplateEditorContent(null);
                        setTemplateEditorError(null);
                        if (id) {
                          try {
                            const t = await fetchEmailTemplate(id);
                            setTemplateEditorContent(t);
                          } catch (err: unknown) {
                            setTemplateEditorError(err instanceof Error ? err.message : "Failed to load template");
                          }
                        }
                      }}
                    >
                      <option value="">—</option>
                      {emailTemplates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.type})
                          {t.builtIn ? " [built-in]" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0 mt-6"
                    onClick={() => {
                      setTemplateEditorNewDraft({
                        name: "",
                        type: "verification",
                        subject: "",
                        html: "<div style=\"font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;\">\n  <p>Your content here. Use {{registrationUrl}} for verification emails.</p>\n</div>",
                        text: "Your content here. Use {{registrationUrl}} for verification emails."
                      });
                      setTemplateEditorId(null);
                      setTemplateEditorContent(null);
                      setTemplateEditorError(null);
                    }}
                  >
                    Create new template
                  </Button>
                </div>
                {templateEditorError && (
                  <Alert variant="destructive">
                    <AlertDescription>{templateEditorError}</AlertDescription>
                  </Alert>
                )}
                {templateEditorContent && (
                  <div className="space-y-4 border rounded p-4">
                    <div className="space-y-2">
                      <Label>Subject</Label>
                      <Input
                        value={templateEditorContent.subject}
                        onChange={(e) =>
                          setTemplateEditorContent((prev) =>
                            prev ? { ...prev, subject: e.target.value } : null
                          )
                        }
                        placeholder="Email subject"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>HTML body</Label>
                      <textarea
                        className="w-full border rounded px-3 py-2 text-sm font-mono min-h-[200px]"
                        value={templateEditorContent.html}
                        onChange={(e) =>
                          setTemplateEditorContent((prev) =>
                            prev ? { ...prev, html: e.target.value } : null
                          )
                        }
                        placeholder="HTML content"
                        spellCheck={false}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Plain text body</Label>
                      <textarea
                        className="w-full border rounded px-3 py-2 text-sm font-mono min-h-[120px]"
                        value={templateEditorContent.text}
                        onChange={(e) =>
                          setTemplateEditorContent((prev) =>
                            prev ? { ...prev, text: e.target.value } : null
                          )
                        }
                        placeholder="Plain text fallback"
                        spellCheck={false}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Use {"{{variable}}"} for placeholders: verification templates support {"{{registrationUrl}}"}, completion templates support {"{{baseUrl}}"}, {"{{formSummary}}"}.
                    </p>
                    <Button
                      disabled={templateEditorSaving}
                      onClick={async () => {
                        if (!templateEditorContent || !templateEditorId) return;
                        setTemplateEditorSaving(true);
                        setTemplateEditorError(null);
                        try {
                          await updateEmailTemplate(templateEditorId, {
                            name: templateEditorContent.name,
                            type: templateEditorContent.type,
                            subject: templateEditorContent.subject,
                            html: templateEditorContent.html,
                            text: templateEditorContent.text
                          });
                          await loadEmailTemplatesList();
                          if (formConfig && showEditor) {
                            setEmailTemplates(await fetchEmailTemplates());
                          }
                        } catch (err: unknown) {
                          setTemplateEditorError(err instanceof Error ? err.message : "Failed to save");
                        } finally {
                          setTemplateEditorSaving(false);
                        }
                      }}
                    >
                      {templateEditorSaving ? "Saving…" : "Save template"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showLogsDialog} onOpenChange={(open) => {
        setShowLogsDialog(open);
        if (!open) {
          setLogsFormConfig(null);
        }
      }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              Drafts & logs{logsFormConfig?.title ? `: ${logsFormConfig.title}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {!logsFormConfig?.publicToken && (
              <Alert>
                <AlertDescription>
                  Save the form to generate a public token before viewing drafts and submissions.
                </AlertDescription>
              </Alert>
            )}
            {logsError && <p className="text-sm text-red-500">{logsError}</p>}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Drafts</CardTitle>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteAllDrafts}
                      disabled={!drafts.length || logsLoading || deletingAllDrafts || !logsFormConfig?.publicToken}
                      className="text-red-600 hover:text-red-700"
                    >
                      {deletingAllDrafts ? "Deleting..." : "Delete all"}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {logsLoading && <p className="text-sm text-gray-500">Loading drafts...</p>}
                  {!logsLoading && drafts.length === 0 && (
                    <p className="text-sm text-gray-500">No drafts yet.</p>
                  )}
                  {!logsLoading && drafts.length > 0 && (
                    <div className="rounded border">
                      <div className="grid grid-cols-[2fr_1fr_1fr_80px] gap-3 p-3 text-xs font-medium text-gray-600">
                        <span>Email</span>
                        <span>Fields</span>
                        <span>Created</span>
                        <span className="text-right">Actions</span>
                      </div>
                      <div className="divide-y">
                        {drafts.map((draft) => (
                          <div
                            key={draft.uid}
                            className="grid grid-cols-[2fr_1fr_1fr_80px] gap-3 p-3 text-xs text-gray-700"
                          >
                            <span className="truncate">{draft.email || "—"}</span>
                            <span>{typeof draft.fieldCount === "number" ? draft.fieldCount : "—"}</span>
                            <span>{formatDate(draft.createdAt)}</span>
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteDraft(draft.uid)}
                                disabled={deletingDraftId === draft.uid || !logsFormConfig?.publicToken}
                                aria-label="Delete draft"
                                className="h-7 w-7 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Submission Logs</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {logsLoading && <p className="text-sm text-gray-500">Loading submissions...</p>}
                  {!logsLoading && submissions.length === 0 && (
                    <p className="text-sm text-gray-500">No submissions yet.</p>
                  )}
                  {!logsLoading && submissions.length > 0 && (
                    <div className="rounded border overflow-hidden">
                      <div className="grid grid-cols-[2fr_1fr_1fr] gap-3 p-3 text-xs font-medium text-gray-600 sticky top-0 z-10 bg-background border-b shrink-0">
                        <span>Email</span>
                        <span>Fields</span>
                        <span>Created</span>
                      </div>
                      <div className="divide-y max-h-[50vh] overflow-y-auto">
                        {submissions.map((submission) => (
                          <div
                            key={submission.uid}
                            className="grid grid-cols-[2fr_1fr_1fr] gap-3 p-3 text-xs text-gray-700"
                          >
                            <span className="truncate">{submission.email || "—"}</span>
                            <span>{typeof submission.fieldCount === "number" ? submission.fieldCount : "—"}</span>
                            <span>{formatDate(submission.createdAt)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PublicFormsManager;
